import type { ProductDto } from '@repo/api/web/models'

/**
 * Чистые билдеры JSON-LD (schema.org) и хелпер вставки в head.
 * Формат — рекомендация Google/Яндекса для rich-сниппетов
 * (SKUBY-SEO-MODULE-SPEC.md §6).
 */

/** Валюта витрины; при мультивалютности переедет в per-channel конфиг */
const PRICE_CURRENCY = 'RUB'

export interface BreadcrumbItem {
  name: string
  /** Абсолютный URL; у последнего элемента (текущая страница) можно опустить */
  url?: string
}

/**
 * Объект <script type="application/ld+json"> для useHead.
 * «<» экранируется, чтобы данные (название товара и т.п.) не могли
 * закрыть тег и внедрить разметку (</script>-инъекция).
 */
export function jsonLdScript(data: unknown) {
  return {
    type: 'application/ld+json',
    innerHTML: JSON.stringify(data).replace(/</g, '\\u003c'),
  }
}

interface JsonLdRating {
  value: number
  count: number
}

interface JsonLdReview {
  author: string
  rating: number
  body?: string
  date: string
}

/** Рейтинг и отзывы товара для JSON-LD; собирает модуль Reviews (`reviewsSeoOpts`) */
export interface ProductJsonLdReviews {
  rating?: JsonLdRating
  reviews?: JsonLdReview[]
}

/** Товар + предложение (цена, валюта, наличие, артикул) + рейтинг и отзывы, если они есть */
export function productJsonLd(
  product: ProductDto,
  opts: { url: string, image?: string, description?: string } & ProductJsonLdReviews,
) {
  const attributes = product.wellKnownAttributes
  const hasRating = !!opts.rating && opts.rating.count > 0

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: attributes?.Name,
    sku: attributes?.Article || undefined,
    description: opts.description || undefined,
    image: opts.image ? [opts.image] : undefined,
    offers: {
      '@type': 'Offer',
      url: opts.url,
      price: attributes?.Price,
      priceCurrency: PRICE_CURRENCY,
      availability: product.inStock
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
    },
    aggregateRating: hasRating
      ? { '@type': 'AggregateRating', ratingValue: opts.rating!.value, reviewCount: opts.rating!.count, bestRating: 5, worstRating: 1 }
      : undefined,
    review: hasRating && opts.reviews?.length
      ? opts.reviews.map(r => ({
          '@type': 'Review',
          author: { '@type': 'Person', name: r.author },
          reviewRating: { '@type': 'Rating', ratingValue: r.rating, bestRating: 5, worstRating: 1 },
          reviewBody: r.body || undefined,
          datePublished: r.date.slice(0, 10),
        }))
      : undefined,
  }
}

/** Хлебные крошки; позиции нумеруются с 1 по порядку элементов */
export function breadcrumbJsonLd(items: BreadcrumbItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      ...(item.url ? { item: item.url } : {}),
    })),
  }
}

/** Организация витрины (для главной) */
export function organizationJsonLd(opts: { siteName: string, origin: string, logo?: string }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: opts.siteName,
    url: opts.origin,
    logo: opts.logo || undefined,
  }
}

/** Сайт + SearchAction на внутренний поиск (для главной) */
export function websiteJsonLd(opts: { siteName: string, origin: string }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: opts.siteName,
    url: opts.origin,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${opts.origin}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  }
}

/** Новость/статья контент-модуля (schema.org NewsArticle) */
export function newsArticleJsonLd(opts: {
  headline: string
  url: string
  description?: string
  image?: string
  datePublished?: string
  dateModified?: string
  siteName?: string
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: opts.headline,
    url: opts.url,
    description: opts.description || undefined,
    image: opts.image ? [opts.image] : undefined,
    datePublished: opts.datePublished || undefined,
    dateModified: opts.dateModified || undefined,
    publisher: opts.siteName
      ? { '@type': 'Organization', name: opts.siteName }
      : undefined,
  }
}
